/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 **/
import { AclModel, FipModel, IpGroupModel, L7BackendServiceModel, L7VirtualServiceModel } from '../../../../models';
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FIPApiService, IpGroupApiService, Layer7ApiService } from '../../../../http';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    LanguageService,
    LogService,
    ModalService,
    NotifyService,
    UtilsService
} from '../../../../../../../core/services';
import { LbErrorService, WizardService, WizardTemplateService, WizardUtilsService } from '../../../../services';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { nameValidation, portRangeValidation } from '../../../../../../../core/directives';
import { pathValidation } from '../../../../directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-l7monitor',
    templateUrl: './servicesMonitor.component.html',
    styleUrls: ['./servicesMonitor.component.scss'],
})

export class ServicesMonitorComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        backends: [],
        condition: []
    };

    /**
     * List of keys of the dropdown items to hide.
     */
    public backendDropdownHiddenContentKeys: string[] = [];

    /**
     * Used to pass value to floating IP component.
     */
    public floatingIPs: FipModel[];

    /**
     * Used to pass value to floating IP component.
     */
    public ipGroups: IpGroupModel[];

    /**
     * Used to set the placeholder.
     */
    public backRealsCount: number[] = [];

    /**
     * Store the floatingIp validation error, so it is accessible from the view.
     */
    public fipValidation: ErrorResponseModel;

    /**
     * A boolean to determine if the form should be full width or not.
     */
    public isFormFullWidth: boolean = false;

    /**
     * Warning validation array.
     */
    public warnings: any [] = [];

    /**
     * List of monitors
     * @private
     */
    private monitors: L7VirtualServiceModel[];

    /**
     * List of backends for the dropdown.
     * @private
     */
    private backends: L7BackendServiceModel[] = [];

    /**
     * List of all acl rules.
     * @private
     */
    private currentAcls: AclModel[] = [];

    /**
     * Create a single tempId so rules can be joined on submission
     * @private
     */
    private tempRuleId: string = 'temp-' + Date.now() + Math.random();

    constructor(
        private fipApiService: FIPApiService,
        private layer7ApiService: Layer7ApiService,
        private ipGroupApiService: IpGroupApiService,
        private notifyService: NotifyService,
        private wizardUtilsService: WizardUtilsService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.wizardService.excludeFromAddAnother = WizardTitleEnum.MONITORS;
        this.setupBaseSubscriber();
        this.setupFormGroup(); // Moved here because unit tests were failing with it in OnInit.
    }

    ngOnInit() {
        this.componentTitle = WizardTitleEnum.MONITORS;
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.setUpModalSubscriber();
        this.initLoading();
    }

    /**
     * Take the form value and convert to frontend model for export.
     * @param formValues
     * @private
     */
    private static convertMonitorToFrontend(formValues: any): L7VirtualServiceModel {
        const fipId = formValues[WizardSubStepTitlesEnum.FLOATING_IPS]['fipId'];
        const fipEnabled = formValues[WizardSubStepTitlesEnum.FLOATING_IPS]['fipEnabled'];
        const general = formValues[WizardSubStepTitlesEnum.GENERAL];

        return {
            fipId,
            fipEnabled: fipEnabled,
            id: general.id,
            name: general.name,
            mode: 'http',
            monitorUri: general.monitorUri,
            type: 'monitor',
            ports: general.ports,
        };
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get rules(): FormArray {
        return this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.rules') as FormArray;
    }

    /**
     * Add the new acl rule to the acl rules array.
     */
    public addRule(rule?: any): void {
        this.rules.push(this.createRulesGroup(rule));
    }

    /**
     * Create additional rules in the form on the fly.
     * @param rule
     */
    public createRulesGroup(rule?: any): FormGroup {
        return this.formBuilder.group({
            id: [rule?.id ? rule.id : 'temp-' + Date.now() + Math.random()],
            backend: [rule?.backend ? rule.backend : null, Validators.required],
            condition: [rule ? rule.condition : null],
            numberOfReals: [rule ? rule.numberOfReals : null, Validators.required],
        });
    }

    /**
     * The add another button will be disabled if the form rule is invalid or there are no more backends.
     */
    public addRuleDisabled(): boolean {
        return this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.rules').invalid ||
            (this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.rules')['controls'].length === this.dropdown.backends.length) &&
            // eslint-disable-next-line max-len
            (this.dropdown.backends.length >= this.backendDropdownHiddenContentKeys.length && !this.backendDropdownHiddenContentKeys.includes(null));
    }

    /**
     * The delete button will be disabled if there is only one rule.
     */
    public deleteRuleDisabled(): boolean {
        return this.rules.length === 1;
    }

    /**
     * Delete an acl rule from the acl rules array by index.
     * @param rule
     * @param index
     */
    public deleteRule(rule: any, index: number): void {
        // remove deleted backend id from the disabled content array.
        const id: string = rule.value?.id;

        if (this.utilsService.isServiceId(id)) {
            this.confirmDeleteACL(index, id);
        } else {
            this.removeRuleFromPage(index);
        }
    }

    /**
     * Confirm delete of acl rule.
     * @param index
     * @param id
     */
    private confirmDeleteACL(index: number, id: string): void {
        const confirmationObject = {
            title: 'acl_delete',
            message: 'acl_delete_message',
            callBack: this.deleteAcl.bind(this, index, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete an acl rule.
     * @param index
     * @param id
     * @private
     */
    private deleteAcl(index: number, id: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.layer7ApiService.deleteAclRule(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.removeRuleFromPage(index);
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('ACLDetailsComponent: deleteAcl() error deleting acl ', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Remove rule from page.
     * @param index
     * @private
     */
    private removeRuleFromPage(index: number): void {
        const rules: FormArray = this.rules;
        const rule: any = rules.controls[index].value;

        // Remove the old line
        rules.removeAt(index);

        this.updateBackendVariables(rule);
        this.updateAclIdsFormValue(rule);

        // // Enabled the last row.
        const aclRulesIndex: number = rules.controls.length - 1;
        rules.controls[aclRulesIndex].enable();
    }

    /**
     * Update backend class variables.
     * @param rule
     * @private
     */
    private updateBackendVariables(rule: any): void {
        const deleteIndex: number = this.backendDropdownHiddenContentKeys?.findIndex(item => item === rule.backend);
        if (deleteIndex !== -1) {
            this.backendDropdownHiddenContentKeys?.splice(deleteIndex, 1);
            this.backRealsCount?.splice(deleteIndex, 1);
        }
    }

    /**
     * Update the form aclIds value.
     * @param rule
     * @private
     */
    private updateAclIdsFormValue(rule: any): void {
        const aclIds: any = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.aclIds').value;
        const deleteAclIndex: number = aclIds?.findIndex((id: string): boolean => id === rule.id);
        if (deleteAclIndex !== -1) {
            aclIds?.splice(deleteAclIndex, 1);
        }
    }

    /**
     * Sets the frontend form value for Fip ID.
     * @param event
     */
    public setFipId(event: any): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.FLOATING_IPS + '.fipId').setValue(event);
    }

    /**
     * Helper function to make changes to the view when the dropdown changes.
     * @param key
     * @param index
     */
    public updateView(key: string, index: number): void {
        if (!this.wizardService.isLoading) {
            // Update the hidden content array by index
            this.backendDropdownHiddenContentKeys[index] = key;
            // Force the dropdown component to update its internal list.
            if (this.dropdown.backends.length <= this.backendDropdownHiddenContentKeys.length) {
                this.backendDropdownHiddenContentKeys = [...this.backendDropdownHiddenContentKeys];
            }

            const backend: L7BackendServiceModel = this.backends.find(item => item.id === key);

            // Update the stored number of rips by index
            if (backend) {
                this.backRealsCount[index] = backend.ripIds.length;
            } else {
                this.logService.error('ServicesMonitorComponent: updateView ', 'Unable to find a backend to process');
            }

            if (this.backRealsCount[index] < this.rules.value[index]?.numberOfReals) {
                this.resetMaxRealsInput(index);
            }
        }
    }

    /**
     * Clear warning.
     * @param i
     */
    public clearWarning(i: number): void {
        this.warnings[i] = null;
    }

    /**
     * When the backend is changed reset the reals count to null.
     * @param index
     * @private
     */
    private resetMaxRealsInput(index: number): void {
        const numberOfReals = this.rules['controls'][index]['controls']['numberOfReals'];
        if (numberOfReals.value) {
            numberOfReals.markAsTouched();
        }
        numberOfReals.setValue(null);
    }

    /**
     * Set up the form builder.
     * @private
     */
    private setupFormGroup(): void {
        // Define our form builder.
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                id: [''], // <- Used outside this component.
                aclIds: [],
                // General
                name: [null, [nameValidation, Validators.required]],
                mode: ['http', Validators.required],
                ports: [80, [Validators.required, portRangeValidation]],
                rules: this.formBuilder.array([this.createRulesGroup()]),
                monitorUri: [null, [Validators.required, pathValidation]],
            }),
            [WizardSubStepTitlesEnum.FLOATING_IPS]: this.formBuilder.group({
                // Floating IP
                fipId: [null, Validators.required],
            }),
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.MONITORS);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    // Validate the form each step of the way.
                    // Use debounce to reduce the number of times the callback is called.
                    this.utilsService.debounce('monitor', () => {
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    });
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: name => {
                    this.checkNameForDuplicates(name, this.monitors);
                }
            });
    }

    /**
     * Setup wizard subscribers.
     * @private
     */
    private setupWizardSubscribers(): void {
        this.wizardService.errorNotificationClosed
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: state => {
                    if (state) {
                        // Clear validation variables
                        this.fipValidation = null;
                        this.serviceForm.get(this.subStepEnum.FLOATING_IPS + '.fipId').updateValueAndValidity();
                    }
                }
            });

        this.wizardService.subStepNameEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: step => {
                    this.isFormFullWidth = step === WizardSubStepTitlesEnum.FLOATING_IPS;
                }
            });
    }

    /**
     * Subscribe to the modal close emitter.
     * @private
     */
    private setUpModalSubscriber(): void {
        // When the missing modal is closed re-init loading to update the floating ip details.
        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modal: any): void => {
                    if (modal.modalId === 'missingIps') {
                        this.initLoading();
                    }
                }
            });
    }

    /**
     * Loads both the backends, floating IP, IP groups and Interfaces and set the values.
     * Tells the wizard service we have finished loading.
     */
    private initLoading(): void {
        const apiCalls = this.getApiCalls();

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    const serviceError = this.getErrorForService(WizardTitleEnum.MONITORS);
                    const template = this.getTemplate(WizardTitleEnum.MONITORS, serviceError);

                    // Don't allow 'add another' if editing.
                    if (this.utilsService.isServiceId(template.id?.value)) {
                        this.wizardService.excludeFromAddAnother = WizardTitleEnum.MONITORS;
                    }

                    this.backends = dataGroup['backends'];
                    this.currentAcls = dataGroup['acls'] || [];

                    const monitors = dataGroup['frontends'].filter(
                        (frontend: L7VirtualServiceModel): boolean => frontend.type === 'monitor'
                    );
                    this.monitors = this.filterServicesByName(template, monitors);
                    this.ipGroups = this.formatIpGroups(dataGroup['ipGroups']);
                    this.floatingIPs = this.formatFloatingIPs(dataGroup['floatingIPs']);

                    // Reset error index as there will only be one service for the monitor component.
                    if (serviceError?.index) {
                        serviceError.index = 0;
                    }

                    this.patchTemplateToForm(template, serviceError?.error);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServicesMonitorComponent:initLoading', 'Init loading failed to complete', error);
                }
            });
    }

    /**
     * Get api calls.
     * @private
     */
    private getApiCalls() {
        const apiCalls = {
            backends: this.layer7ApiService.getBackends(),
            frontends: this.layer7ApiService.getFrontends(),
            groups: this.ipGroupApiService.getGroups(),
            floatingIPs: this.fipApiService.getFloatingIPs()
        };

        const monitorId = this.utilsService.getObjectValue(
            this.wizardService.controllerState[WizardTitleEnum.MONITORS], '0.id.value'
        );

        if (monitorId && this.utilsService.isServiceId(monitorId)) {
            apiCalls['acls'] = this.layer7ApiService.getFrontendAcls(monitorId);
        }

        return apiCalls;
    }

    /**
     * Populate the form with template values.
     * @param template
     * @param serviceError
     * @private
     */
    private patchTemplateToForm(template: any, serviceError: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdownContent(template);

        template.monitorUri.value = decodeURI(template.monitorUri.value);

        // Populate our patch
        const templatePatch = this.createTemplatePatch(template);

        if (templatePatch.aclIds?.length) {
            this.setUpRules(templatePatch.aclIds); // Creates an empty form field for each ACL.
            templatePatch.rules = this.getRulesFromAcls(templatePatch);
        }

        if (templatePatch['aclIds']) {
            if (templatePatch['aclIds'].includes('$1')) {
                templatePatch['aclIds'] = [this.tempRuleId];
            }
        }

        if (!templatePatch['fipId']) {
            templatePatch['fipId'] = this.serviceForm.get(WizardSubStepTitlesEnum.FLOATING_IPS + '.fipId').value;
        }

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.MONITORS);

        const timerId = setTimeout(() => {
            // Apply patch to form
            this.applyPatchAndValidate(templatePatch);

            // Check the name after patching
            this.checkNameForDuplicates(template.name.value, this.monitors, 'name', true);

            // Wizard errors take precedence over internal errors.
            if (!serviceError) {
                this.checkTemplateForInvalidRules(templatePatch, this.backends);
            } else {
                this.notifyService.removeNotificationById('wizardWarning');
            }

            // Patching can change the view, so we should kick the change detector after patch.
            this.cdr.detectChanges();

            // Tell the wizard to show the content
            this.wizardService.isLoading = false;

            // Once patched output the values to the controller state.
            this.outputFormValues();

            // Once everything is loaded check to see if there are any error in the controller state
            this.checkForErrorState(serviceError);

            clearTimeout(timerId);
        }, 250);
    }

    /**
     * Check for error state.
     * @param errorArray
     * @private
     */
    private checkForErrorState(errorArray: any[]): void {
        this.loadedWithErrors(errorArray);

        if (errorArray?.length) {
            const index = errorArray.findIndex((item: any) => item.field === 'port');
            if (index !== -1) {
                errorArray[index].field = 'ports';
            }

            this.setRuleValidationFromErrorObject(errorArray);
            this.fipValidation = this.lbErrorService.getFloatingIpValidationFromErrorObject(errorArray);
        }
    }

    /**
     * Set up rules.
     * @private
     * @param rules
     */
    private setUpRules(rules: any): void {
        this.rules.removeAt(0);

        for (const rule of rules) {
            this.addRule(rule);
        }
    }

    /**
     * Sets up the acls depending on add/edit/template and stores the backend/redirect-location ids.
     * @param templatePatch
     * @private
     */
    private getRulesFromAcls(templatePatch: any): any[] {
        if (templatePatch.aclIds.includes('$1') || templatePatch.aclIds.some((item: string): boolean => item.startsWith('temp'))) {
            const acls: any[] = this.getAclsFromControllerState();

            return this.formatTemplateAclsValues(acls);
        } else {
            // If editing just return the acl ids
            const aclModels: AclModel[] = this.currentAcls.filter(item => templatePatch.aclIds.includes(item.id));

            return this.formatTemplateForAddEdit(aclModels);
        }
    }

    /**
     * Get ACLs from controller state.
     * @private
     */
    private getAclsFromControllerState(): any {
        const templates: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${[WizardTitleEnum.ACLS]}`);

        const acls: any[] = [];
        for (const template of templates) {
            acls.push(this.createTemplatePatch(template));
        }

        return acls;

    }

    /**
     * Format template ACLs values for the form.
     * @param acls
     * @private
     */
    private formatTemplateAclsValues(acls: any): any[] {
        const backendReferences: any[] = this.utilsService.getObjectValue(
            this.wizardService.selectedTemplate, 'backendReferences'
        ) || [];

        const numberOfBackends: number = this.backends.filter((item: L7BackendServiceModel): boolean => item.mode === 'http').length;
        const storedBackends: any = this.utilsService.duplicateObject(this.backends);

        const rules: any[] = [];
        const hiddenBackendKeys: string[] = [];
        let index: number = 0;
        for (const acl of acls) {
            if (index < numberOfBackends) {
                const backendId: string = this.utilsService.isServiceId(acl.redirectLocation)
                    ? acl.redirectLocation
                    : backendReferences.length && backendReferences[index]?.id;

                const matchedIndex: number = storedBackends.findIndex(
                    (item: L7BackendServiceModel): boolean => item.id === backendId || item.name === acl.conditionParam
                );

                let matchedBackend: L7BackendServiceModel;
                if (matchedIndex !== -1) {
                    matchedBackend = storedBackends[matchedIndex];
                    storedBackends.splice(matchedIndex, 1);
                }

                if (matchedBackend?.id) {
                    hiddenBackendKeys.push(matchedBackend.id);
                }

                const numberOfReals: any = this.utilsService.getObjectValue(acl, 'conditionValues.0');
                this.backRealsCount.push(numberOfReals);

                rules.push({
                    id: 'temp-' + Date.now() + Math.random(),
                    backend: matchedBackend?.id || null,
                    condition: matchedBackend ? acl?.condition : null,
                    numberOfReals: matchedBackend ? numberOfReals : null
                });
            }

            index++;
        }

        this.backendDropdownHiddenContentKeys = hiddenBackendKeys;

        return rules;
    }

    /**
     * Format acls for add/edit.
     * @param acls
     * @private
     */
    private formatTemplateForAddEdit(acls: AclModel[]): any {
        const patchValues: any[] = [];
        const hiddenBackendKeys: string[] = [];
        for (const acl of acls) {
            const backendId: string = acl.redirectLocation;
            const matchedBackend: L7BackendServiceModel = this.backends.find(
                (item: L7BackendServiceModel): boolean => item.id === backendId
            ) || null;

            if (matchedBackend?.id) {
                hiddenBackendKeys.push(matchedBackend.id);
            }

            patchValues.push({
                id: acl.id,
                backend: backendId,
                condition: acl.condition,
                numberOfReals: acl.conditionValues
            });
        }

        this.backendDropdownHiddenContentKeys = hiddenBackendKeys;

        return patchValues;
    }

    /**
     * Check the template for any invalid rules.
     * @private
     */
    private checkTemplateForInvalidRules(templatePatch: any, backends: any[]): void {
        if (templatePatch.aclIds?.includes('$1') && !templatePatch.fipId?.length) {
            return;
        }

        const backendReferences: any[] = this.utilsService.getObjectValue(
            this.wizardService.selectedTemplate, 'backendReferences'
        ) || [];

        const missingBackends: any[] = this.getMissingBackends(backends, backendReferences);
        if (missingBackends.length) {
            this.setErrorOnInvalidRule(backends, backendReferences);
        }

        const misMatchedCounts: any[] = this.checkRealServerCounts(backends);

        if (missingBackends.length || misMatchedCounts.length) {
            this.goToErroredStep('Monitor', 0);
        }
    }

    /**
     * Check that the monitor template isn't missing any backends by name or id.
     * @param backends
     * @param backendReferences
     * @private
     */
    private getMissingBackends(backends: L7BackendServiceModel[], backendReferences: any[]): any[] {
        return backendReferences.filter(
            backendRef => !backends.find(
                (backend: L7BackendServiceModel): boolean =>
                    backend.id === backendRef.id && backend.name.toLowerCase() === backendRef.name.toLowerCase()
            )
        );
    }

    /**
     * Set invalid error for invalid rules.
     * @param backends
     * @param backendReferences
     * @private
     */
    private setErrorOnInvalidRule(backends: any[], backendReferences: any[]): void {
        const controls = this.rules['controls'];

        this.updatedDropdowns(controls);
        this.notifyIfMoreRulesThanBackends(backends, backendReferences);
        this.validateControls(backends, controls, backendReferences);

        this.backendDropdownHiddenContentKeys = [...this.backendDropdownHiddenContentKeys];
    }

    /**
     * Update dropdowns.
     * @param controls
     * @private
     */
    private updatedDropdowns(controls: any[]): void {
        if (this.dropdown.backends.length < 2 || this.dropdown.backends.length < controls.length) {
            this.dropdown.backends.unshift({ key: '- Select -', value: '- Select -' });
        }
    }

    /**
     * Notify the user if there are more rules than backends.
     * @param backends
     * @param backendReferences
     * @private
     */
    private notifyIfMoreRulesThanBackends(backends: any[], backendReferences: any[]): void {
        const httpsBackends = backends.filter(item => item.mode === 'http');

        if (httpsBackends.length < backendReferences.length) {
            const s = httpsBackends.length > 1 ? 's' : '';
            const rulesToDelete = backendReferences.length - httpsBackends.length;
            const haveHas = rulesToDelete > 1 ? 'have' : 'has';

            this.notifyService.error(
                // eslint-disable-next-line max-len
                `This template contains ${backendReferences.length} rules but only ${httpsBackends.length} http backend${s}. ${rulesToDelete} rule${rulesToDelete > 1 ? 's' : ''} ${haveHas} been deleted.`,
                { id: 'wizardWarning', wizardError: 'wizardError' }
            );
        }
    }

    /**
     * Loop through the controls and validate them.
     * @param backends
     * @param controls
     * @param backendReferences
     * @private
     */
    private validateControls(backends: any[], controls: any[], backendReferences: any[]): void {
        this.warnings = Array(controls.length);

        for (let i: number = 0; i < controls.length; i++) {
            const backendControl = controls[i]['controls']['backend'];

            if (!backendControl.value) {
                backendControl.setValue(null);

                const matchedBackendByName = this.hasBackendLabel(backends, backendReferences[i].name);
                if (matchedBackendByName) {
                    backendControl.setValue(matchedBackendByName.id);
                    this.warnings[i] = 'The wizard has matched this by its name, however the ID differs from the ID in the template.';
                } else {
                    controls[i].enable();
                    backendControl.setValue('- Select -');
                    const timerId = setTimeout(() => {
                        backendControl.setErrors(
                            { 'invalidBackend': 'The template backend no longer exists. Please choose another' }
                        );
                        backendControl.markAsTouched();
                        clearInterval(timerId);
                    }, 50);
                }
            }
        }
    }

    /**
     * Check if name exists.
     * @param backends
     * @param missingName
     * @private
     */
    private hasBackendLabel(backends: any[], missingName: any): any {
        return backends.find((backend: any): boolean => backend.name === missingName);
    }

    /**
     * Check real server counts.
     * @param backends
     * @private
     */
    private checkRealServerCounts(backends: any[]): any[] {
        const misMatchedCounts = [];
        let index = 0;
        const controls = this.rules['controls'];
        for (const control of controls) {
            const backendId = control['controls']['backend'].value;
            const backend = backends.find(item => item.id === backendId);
            if (backend?.ripIds?.length < this.backRealsCount[index]) {
                this.backRealsCount[index] = backend?.ripIds?.length;
                const numberOfReals = control['controls']['numberOfReals'];
                // Mark the numberOfReals as touched in case the number is invalid.
                numberOfReals.markAsTouched();
                misMatchedCounts.push(backendId);
            }

            index++;
        }

        return misMatchedCounts;
    }

    /**
     * Go to the section that errored.
     * @param sectionName
     * @param subStepIndex
     * @private
     */
    private goToErroredStep(sectionName: string, subStepIndex: number): void {
        const index = this.wizardUtilsService.getSectionIndex(sectionName, this.wizardService.wizardSteps);

        this.wizardService.goToStep(index, subStepIndex);
    }

    /**
     * Loop through rules and mark any that are in the errorArray.
     * @param errorArray
     * @private
     */
    private setRuleValidationFromErrorObject(errorArray: any[]): void {
        const erroredBackends = errorArray.filter(error => error.field === 'backend');
        if (erroredBackends.length) {
            this.findErroredFieldInForm(erroredBackends, 'numberOfReals');
        }

        const erroredConditionValues = errorArray.filter(error => error.field === 'conditionValues');
        if (erroredConditionValues.length) {
            this.findErroredFieldInForm(erroredConditionValues, 'numberOfReals');
        }
    }

    /**
     * Find errored fields.
     * @param filteredError
     * @param field
     * @private
     */
    private findErroredFieldInForm(filteredError: any[], field: string,) {
        const errorValues = filteredError.map(item => item.value);
        for (const rule of this.rules.controls) {
            const erroredField = rule.get(field);
            const errorIndex = errorValues.findIndex(item => item === erroredField.value);
            if (errorIndex !== -1) {
                erroredField.markAsTouched();
                erroredField.setErrors({ 'responseError': filteredError[errorIndex].message });
            }
        }
    }

    /**
     * Setup dropdown arrays object.
     * @param template
     * @private
     */
    private setUpDropdownContent(template: any): void {
        this.dropdown.backends = this.getDropDownBackends(template);
        this.dropdown.condition = [
            <DropdownModel>{ key: 'lt', value: this.languageService.translate('services_less_than') },
            <DropdownModel>{ key: 'le', value: this.languageService.translate('services_less_than_equals') },
            <DropdownModel>{ key: 'ge', value: this.languageService.translate('services_greater_than_equals') },
            <DropdownModel>{ key: 'gt', value: this.languageService.translate('services_greater_than') }
        ];
    }

    /**
     * Return dropdown structure array for backends.
     * @param template
     * @private
     */
    private getDropDownBackends(template: any): DropdownModel[] {
        const convertedBackends = [];
        if (!this.utilsService.isServiceId(template.id?.value)) {
            convertedBackends.push({ key: '- Select -', value: '- Select -' });
        }

        for (const backend of this.backends) {
            if (backend.mode === 'http') {
                convertedBackends.push(<DropdownModel>{ key: backend.id, value: backend.name });
            }
        }

        return convertedBackends;
    }

    /**
     * Output servicesMonitor form values as Monitor Model.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            const frontOj = this.flattenServiceFormValues(<L7VirtualServiceModel>{});
            Object.assign(frontOj, ServicesMonitorComponent.convertMonitorToFrontend(this.serviceForm.value));

            if (!Array.isArray(frontOj.ports)) {
                frontOj.ports = [frontOj.ports];
            }

            frontOj.monitorUri = encodeURI(frontOj.monitorUri);

            if (this.serviceForm.get('general.rules')['controls'][0].value.backend) {
                // Using "getRawValue" here to include the disabled fields that are intentionally disabled
                this.wizardService.controllerState[WizardTitleEnum.ACLS] =
                    this.convertMonitorRulesToAcls(this.serviceForm.getRawValue().general.rules) || [];
            }

            if (frontOj.aclIds && frontOj.rules) {
                frontOj.aclIds = frontOj.rules.map((item: any) => item.id);
            }

            if (frontOj.rules) {
                delete frontOj.rules;
            }

            // Loop through form values
            const template = this.wizardService.controllerState[WizardTitleEnum.MONITORS][this.templateIndex];

            for (const property in frontOj) {
                if (frontOj.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property] && this.inputStateStore[property].hidden;
                    template[property] = {
                        value: frontOj[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }

    /**
     * Take the form value and convert to acls model for export.
     * @param rules
     * @private
     */
    private convertMonitorRulesToAcls(rules: any): AclModel[] {
        const serviceId = this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.MONITORS}.${this.templateIndex}.id.value`);

        const aclRules = [];
        for (const rule of rules) {
            let numberOfReals = typeof rule.numberOfReals === 'number' ? rule.numberOfReals.toString() : rule.numberOfReals;
            if (!Array.isArray(numberOfReals)) {
                numberOfReals = [rule.numberOfReals];
            }

            aclRules.push({
                id: { value: rule.id ? rule.id : null },
                conditionType: { value: 'nbsrv' },
                conditionParam: { value: rule.backend },
                redirectType: { value: 'none' },
                condition: { value: rule.condition },
                conditionValues: { value: numberOfReals },
                redirectLocation: { value: rule.backend },
                serviceId: { value: serviceId ? serviceId : '$1' },
            });
        }

        return aclRules;
    }
}
